import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnChanges } from '@angular/core';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { CampaignProgress } from '../../../models/campaign.models';
import { parseLocalDateString } from '../../../shared/date.util';

/**
 * Campaign goal tracking and results from the saved progress summary.
 * `goals` draws won customers and revenue against their targets and the pace the
 * campaign dates require; `results` draws the funnel and weekly logged contact.
 */
@Component({
  selector: 'app-campaign-goal-charts',
  standalone: true,
  imports: [CommonModule, NgChartsModule],
  templateUrl: './campaign-goal-charts.component.html',
  styleUrls: ['./campaign-progress-charts.component.css'],
})
export class CampaignGoalChartsComponent implements OnChanges {
  @Input() progress: CampaignProgress | null = null;
  @Input() loading = false;
  @Input() view: 'goals' | 'results' = 'goals';
  @Input() targetCustomers: number | null = null;
  @Input() targetRevenue: number | null = null;
  @Input() startsOn = '';
  @Input() endsOn = '';
  customers: ChartData<'line'> = { labels: [], datasets: [] };
  revenue: ChartData<'line'> = { labels: [], datasets: [] };
  funnel: ChartData<'bar'> = { labels: [], datasets: [] };
  activity: ChartData<'bar'> = { labels: [], datasets: [] };
  lineOptions: ChartOptions<'line'> = {};
  funnelOptions: ChartOptions<'bar'> = {};
  activityOptions: ChartOptions<'bar'> = {};
  funnelSummary = '';
  activitySummary = '';
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  get hasWeeks(): boolean { return !!this.progress?.weeks.length; }

  ngOnChanges(): void {
    const style = getComputedStyle(this.document.documentElement);
    const color = (name: string) => style.getPropertyValue(`--br-color-${name}`).trim();
    const muted = color('muted'), text = color('text'), grid = color('border-soft');
    const axes = {
      x: { ticks: { color: muted }, grid: { display: false } },
      y: { beginAtZero: true, ticks: { color: muted, precision: 0 }, grid: { color: grid } },
    };
    const legend = { position: 'bottom' as const, labels: { color: text, usePointStyle: true, boxWidth: 8 } };
    this.lineOptions = { responsive: true, maintainAspectRatio: false, animation: false, scales: axes,
      plugins: { legend }, elements: { point: { radius: 2 } } };
    this.activityOptions = { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend },
      scales: { x: { ...axes.x, stacked: true }, y: { ...axes.y, stacked: true } } };
    this.funnelOptions = { responsive: true, maintainAspectRatio: false, animation: false, indexAxis: 'y',
      plugins: { legend: { display: false } },
      scales: { x: { beginAtZero: true, ticks: { color: muted, precision: 0 }, grid: { color: grid } },
        y: { ticks: { color: text }, grid: { display: false } } } };
    const progress = this.progress;
    if (!progress) return;

    const labels = progress.weeks.map(week => week.weekStart.slice(5));
    let customers = 0, revenue = 0;
    const wonByWeek = progress.weeks.map(week => customers += week.won);
    const revenueByWeek = progress.weeks.map(week => revenue += week.wonRevenue);
    const pace = this.paceFractions(progress.weeks.map(week => week.weekStart));
    const goalLines = (target: number | null, label: string) => target == null ? [] : [
      { label: `${label} goal`, data: labels.map(() => target), borderColor: muted, borderDash: [6, 4], pointRadius: 0, fill: false },
      ...(pace ? [{ label: 'Needed by now', data: pace.map(fraction => Math.round(target * fraction * 100) / 100),
        borderColor: color('warning'), borderDash: [2, 3], pointRadius: 0, fill: false }] : []),
    ];
    this.customers = { labels, datasets: [
      { label: 'New customers', data: wonByWeek, borderColor: color('success'), backgroundColor: color('success'), fill: false },
      ...goalLines(this.targetCustomers, 'Customer'),
    ] };
    this.revenue = { labels, datasets: [
      { label: 'Won revenue', data: revenueByWeek, borderColor: color('primary'), backgroundColor: color('primary'), fill: false },
      ...goalLines(this.targetRevenue, 'Revenue'),
    ] };

    const steps = [['In campaign', progress.targets], ['Contacted', progress.contacted],
      ['Quoted', progress.quoted], ['Won', progress.won]] as const;
    this.funnelSummary = steps.map(([label, count]) => `${label}: ${count}`).join('. ');
    this.funnel = { labels: steps.map(([label]) => label), datasets: [{ label: 'Leads', data: steps.map(([, count]) => count),
      backgroundColor: [color('primary-soft'), color('primary'), color('warning'), color('success')], borderRadius: 4, maxBarThickness: 28 }] };

    const kinds = [['Calls', 'calls', 'primary'], ['Visits', 'visits', 'success'],
      ['Emails', 'emails', 'warning'], ['Meetings', 'meetings', 'muted']] as const;
    this.activitySummary = kinds.map(([label, key]) =>
      `${label}: ${progress.weeks.reduce((sum, week) => sum + week[key], 0)}`).join('. ');
    this.activity = { labels, datasets: kinds.map(([label, key, tone]) => ({
      label, data: progress.weeks.map(week => week[key]), backgroundColor: color(tone), borderRadius: 2, maxBarThickness: 24 })) };
  }

  /** Share of the campaign elapsed at the end of each week, when both dates are set. */
  private paceFractions(weekStarts: readonly string[]): number[] | null {
    const start = parseLocalDateString(this.startsOn), end = parseLocalDateString(this.endsOn);
    if (!start || !end || end < start) return null;
    const total = end.getTime() - start.getTime() + 86400000;
    return weekStarts.map(week => {
      const weekEnd = parseLocalDateString(week)!.getTime() + 7 * 86400000;
      return Math.max(0, Math.min(1, (weekEnd - start.getTime()) / total));
    });
  }
}
